import { ArrowUpRightIcon } from 'lucide-react'

import { isCurrentPlanOffThatch } from 'components/member_onboarding_v2/utils'
import { Banner, TextButton } from 'slab'
import {
  type MemberOnboardingMemberPlanChangesProps,
} from 'utils/web_api.schemas'

import RenewalsPlanComparisonHeader from '../RenewalsPlanComparisonHeader/RenewalsPlanComparisonHeader'

export interface QLEMedicalPlanCardProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  hasFamily: boolean;
}

export const QLEMedicalPlanCard = ({
  planChangeSummary,
  hasFamily,
}: QLEMedicalPlanCardProps) => {
  const currentYear = planChangeSummary.current_plan_year
  const nextYear = planChangeSummary.next_plan_year
  const currentMedicalPlan = currentYear.medical_plan
  const nextYearMedicalPlan = nextYear.medical_plan

  const offThatchOrMedicare = isCurrentPlanOffThatch(planChangeSummary)

  // Use current year plan if medical plan is off-thatch/medicare, otherwise use next year plan
  const planToUse = offThatchOrMedicare ? currentMedicalPlan : nextYearMedicalPlan
  const yearToUse = offThatchOrMedicare ? currentYear : nextYear

  if (!planToUse) {
    return null
  }

  // Get the values to display
  const monthlyPremium = planToUse.premium_amount.formatted_amount
  const deductible = hasFamily
    ? planToUse.family_deductible
    : planToUse.deductible
  const outOfPocketMax = hasFamily
    ? planToUse.family_out_of_pocket_max
    : planToUse.out_of_pocket_max

  // Don't show "View full plan details" link for off-thatch/medicare plans
  const viewFullPlanDetailsUrl = planToUse && !offThatchOrMedicare
    ? `/member_onboarding/progress?step=renewal_next_year_medical_plan_details:${planToUse.plan_code}`
    : undefined

  return (
    <div className='flex flex-col gap-6 border border-gray-200 rounded-2xl shadow-sm p-4 xs:p-6'>
      <RenewalsPlanComparisonHeader
        plan={planToUse}
        planType='medical'
      />
      {offThatchOrMedicare && (
        <Banner
          title={`You purchased this plan outside of Thatch — reference your plan for ${
            yearToUse?.plan_year ? yearToUse.plan_year + ' ' : ''
          }details`}
          type='notice'
        />
      )}
      <div className={`grid gap-4 ${offThatchOrMedicare ? 'grid-cols-1' : 'grid-cols-3'}`}>
        <div className='flex flex-col'>
          <div className='body-sm text-gray-500 mb-2'>Monthly premium</div>
          <div className='body text-gray-800'>{monthlyPremium}</div>
        </div>
        {!offThatchOrMedicare && (
          <>
            <div className='flex flex-col'>
              <div className='body-sm text-gray-500 mb-2'>
                {hasFamily ? 'Family deductible' : 'Deductible'}
              </div>
              <div className='body text-gray-800'>{deductible}</div>
            </div>
            <div className='flex flex-col'>
              <div className='body-sm text-gray-500 mb-2'>
                {hasFamily ? 'Family out of pocket max' : 'Out of pocket max'}
              </div>
              <div className='body text-gray-800'>{outOfPocketMax}</div>
            </div>
          </>
        )}
      </div>
      {viewFullPlanDetailsUrl && (
        <TextButton
          onClick={() => {
            window.open(viewFullPlanDetailsUrl, '_blank')
          }}
          type='button'
          rightIcon={ArrowUpRightIcon}
        >
          View full plan details
        </TextButton>
      )}
    </div>
  )
}
